import { notify } from "@/components/common/Notification";
import SchemaFormRender from "@/components/Schema/SchemaFormRender";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { DynamicModuleService, SchemaService } from "@/services/APIs";
import { useQuery } from "@tanstack/react-query";
import { forwardRef, useEffect, useImperativeHandle } from "react";

const SchemaFormAutoSubmit = forwardRef(
  (
    {
      form,
      schemaName,
      recordId,
    }: {
      form: any;
      schemaName?: string;
      recordId?: string;
    },
    ref
  ) => {
    const { data: schemaJson } = useQuery({
      queryKey: ["schema", schemaName],
      queryFn: () => SchemaService.getSchemaByName(schemaName ?? ""),
      enabled: !!schemaName,
    });
    const isEditMode = !!recordId;

    const { data: recordInfo } = useQuery({
      queryKey: ["dynamic-model", schemaName, recordId],
      queryFn: () =>
        DynamicModuleService.getItemById(schemaName ?? "", recordId ?? ""),
      enabled: isEditMode && !!schemaName && !!recordId,
    });

    const handleSave = async () => {
      try {
        await form.validate();
        const values = removeMetaFields(form.values);

        // Recursively flatten all field values from form.values
        // This ensures fields like m0jxt9qkjqb are included even if they're nested
        const flattenFormValues = (
          obj: any,
          result: Record<string, any> = {},
          path: string[] = []
        ): Record<string, any> => {
          if (!obj || typeof obj !== "object") {
            return result;
          }

          if (Array.isArray(obj)) {
            // For arrays, use the parent path as the key
            if (path.length > 0) {
              const key = path[path.length - 1];
              result[key] = obj;
            }
            return result;
          }

          Object.keys(obj).forEach(key => {
            const value = obj[key];
            const currentPath = [...path, key];

            // Skip layout container keys (they're just wrappers)
            const isLayoutKey =
              key === "layoutContainer" ||
              key === "basicInfo" ||
              key === "propertyDetails" ||
              key === "financialDetails" ||
              key === "additionalInfo" ||
              key === "basicGrid" ||
              key === "propertyGrid" ||
              key === "financialGrid";

            if (
              isLayoutKey &&
              value &&
              typeof value === "object" &&
              !Array.isArray(value)
            ) {
              // Recursively process nested layout containers
              flattenFormValues(value, result, currentPath);
            } else if (value !== null && value !== undefined) {
              // This is a field value - add it to result using the key directly
              // Formily should have set the name property, so the key should be the field name
              if (typeof value === "object" && !Array.isArray(value)) {
                // If it's still an object, try to flatten it further
                flattenFormValues(value, result, currentPath);
              } else {
                // Primitive or array value - this is the actual field value
                result[key] = value;
              }
            }
          });

          return result;
        };

        // Flatten all form values
        const flattenedValues = flattenFormValues(form.values);

        // Helper function to convert date from DD-MM-YYYY to YYYY-MM-DD
        const convertDateFormat = (dateString: any): any => {
          if (!dateString || typeof dateString !== "string") {
            return dateString;
          }

          // Check if it's in DD-MM-YYYY format (e.g., "29-03-2025")
          const ddMMyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
          const match = dateString.match(ddMMyyyyPattern);

          if (match) {
            const [, day, month, year] = match;
            return `${year}-${month}-${day}`;
          }

          // If it's already in YYYY-MM-DD format or another format, return as is
          return dateString;
        };

        // Merge flattened values into values, prioritizing direct values from form.values
        Object.keys(flattenedValues).forEach(key => {
          if (
            flattenedValues[key] !== undefined &&
            flattenedValues[key] !== null
          ) {
            // Only override if the key doesn't exist in values, or if the flattened value is more complete
            if (
              !(key in values) ||
              (Array.isArray(flattenedValues[key]) &&
                flattenedValues[key].length > 0)
            ) {
              let value = flattenedValues[key];

              // Convert date fields from DD-MM-YYYY to YYYY-MM-DD
              // Check if the key is a date field (common date field names)
              const isDateField =
                key.toLowerCase().includes("date") ||
                key.toLowerCase().includes("enddate") ||
                key.toLowerCase() === "date";

              if (isDateField && typeof value === "string") {
                value = convertDateFormat(value);
              }

              values[key] = value;
            }
          }
        });

        // Also convert date fields in the direct values object
        Object.keys(values).forEach(key => {
          const isDateField =
            key.toLowerCase().includes("date") ||
            key.toLowerCase().includes("enddate") ||
            key.toLowerCase() === "date";

          if (isDateField && typeof values[key] === "string") {
            values[key] = convertDateFormat(values[key]);
          }
        });

        // Debug: Log form values to help diagnose issues
        console.log(
          "[SchemaFormAutoSubmit] Form values before save:",
          JSON.stringify(form.values, null, 2)
        );
        console.log(
          "[SchemaFormAutoSubmit] Flattened values:",
          JSON.stringify(flattenedValues, null, 2)
        );
        console.log(
          "[SchemaFormAutoSubmit] Final values to be saved:",
          JSON.stringify(values, null, 2)
        );

        // Debug: Log the exact payload being sent (can be removed in production)
        console.log(
          "[SchemaFormAutoSubmit] Payload being sent to API:",
          JSON.stringify(values, null, 2)
        );
        console.log(
          "[SchemaFormAutoSubmit] All keys in payload:",
          Object.keys(values)
        );

        if (isEditMode) {
          // Check for changes before updating
          if (!checkFormChanges(values, recordInfo, notify)) {
            return;
          }
          const response = await DynamicModuleService.updateItem(
            schemaName ?? "",
            recordId ?? "",
            values
          );
          console.log("[SchemaFormAutoSubmit] Update response:", response);
          if (!response) {
            throw new Error("Failed to update item");
          }
        } else {
          const response = await DynamicModuleService.createItem(
            schemaName ?? "",
            values
          );
          console.log("[SchemaFormAutoSubmit] Create response:", response);
          if (!response) {
            throw new Error("Failed to create item");
          }
        }
      } catch (error: any) {
        // Extract error message from API response
        let errorMessage = "An error occurred while saving";

        // Log full error details for debugging
        console.error("[SchemaFormAutoSubmit] Full error object:", error);
        console.error(
          "[SchemaFormAutoSubmit] Error response:",
          error?.response
        );
        console.error(
          "[SchemaFormAutoSubmit] Error response data:",
          error?.response?.data
        );

        if (error?.response?.data) {
          const data = error.response.data;

          // Try different possible error message formats
          if (data?.error?.message) {
            errorMessage = data.error.message;
          } else if (data?.message) {
            errorMessage = data.message;
          } else if (typeof data?.error === "string") {
            errorMessage = data.error;
          } else if (
            data?.non_field_errors &&
            Array.isArray(data.non_field_errors) &&
            data.non_field_errors.length > 0
          ) {
            errorMessage = data.non_field_errors[0];
          } else if (typeof data === "string") {
            // If it's a generic error message, provide more context
            if (
              data.toLowerCase().includes("invalid input") ||
              data.toLowerCase().includes("validation")
            ) {
              errorMessage = `${data} Please check that all required fields are filled correctly and data formats are valid.`;
            } else {
              errorMessage = data;
            }
          } else if (data && typeof data === "object") {
            // If there are field-specific errors, format them
            const fieldErrors: string[] = [];
            Object.keys(data).forEach(key => {
              if (Array.isArray(data[key]) && data[key].length > 0) {
                fieldErrors.push(`${key}: ${data[key].join(", ")}`);
              } else if (typeof data[key] === "string" && data[key]) {
                fieldErrors.push(`${key}: ${data[key]}`);
              }
            });
            if (fieldErrors.length > 0) {
              errorMessage = fieldErrors.join("; ");
            } else {
              // If we can't extract a specific message, use a more descriptive default
              errorMessage = `Validation failed: ${JSON.stringify(data)}`;
            }
          }
        } else if (error?.message) {
          errorMessage = error.message;
        }

        // If we still have a generic error, add more context
        if (
          errorMessage === "Invalid input." ||
          errorMessage.toLowerCase().includes("invalid input")
        ) {
          errorMessage =
            "Invalid input. Please check that all required fields are filled correctly and data formats are valid. Check the console for details.";
          console.error(
            "[SchemaFormAutoSubmit] Payload that failed:",
            JSON.stringify(values, null, 2)
          );
        }

        console.error(
          "[SchemaFormAutoSubmit] Extracted error message:",
          errorMessage
        );
        throw new Error(errorMessage);
      }
    };

    useImperativeHandle(ref, () => ({
      handleSave,
    }));

    useEffect(() => {
      if (isEditMode && recordInfo) {
        form.setInitialValues(removeMetaFields(recordInfo));
      }
    }, [isEditMode, recordInfo, form]);

    return (
      <SchemaFormRender
        form={form}
        schemaJson={schemaJson?.json ?? schemaJson}
      />
    );
  }
);

export default SchemaFormAutoSubmit;
